import { useState, useRef, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { sendConciergeMessage, fetchRequests } from '@/lib/api';
import { Link } from 'react-router-dom';
import { StatusDot } from '@/components/shared/StatusDot';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Bot, Send, Inbox, MessageSquare, Sparkles, ExternalLink, Key, Shield, Download, Laptop, UserPlus, ShieldAlert, Mail, FileKey, HelpCircle, Star, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const CATALOG_ICONS = { 'key': Key, 'shield': Shield, 'download': Download, 'laptop': Laptop, 'user-plus': UserPlus, 'shield-alert': ShieldAlert, 'mail': Mail, 'file-key': FileKey, 'help-circle': HelpCircle };
const CATALOG_COLORS = { 'Access': '#06B6D4', 'Software': '#6366F1', 'Hardware': '#F59E0B', 'Communication': '#10B981', 'General': '#94A3B8' };

const QUICK_ACTIONS = [
  { text: 'I need a password reset', intent: 'PASSWORD_RESET' },
  { text: 'Request VPN access', intent: 'VPN_ACCESS' },
  { text: 'I need a new laptop', intent: 'LAPTOP_REQUEST' },
  { text: 'Install software', intent: 'SOFTWARE_INSTALL' },
  { text: 'Set up Slack access', intent: 'EMAIL_SETUP' },
  { text: 'Check my ticket status', intent: 'STATUS_CHECK' },
];

// Persist session across page refreshes
const getSessionId = () => {
  let sid = sessionStorage.getItem('concierge_session');
  if (!sid) { sid = `session-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`; sessionStorage.setItem('concierge_session', sid); }
  return sid;
};

export default function RequestsPage() {
  const [sessionId] = useState(getSessionId);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [showCatalog, setShowCatalog] = useState(false);
  const [surveyTicket, setSurveyTicket] = useState(null);
  const chatEndRef = useRef(null);
  const queryClient = useQueryClient();
  const token = localStorage.getItem('nexus_token');

  const { data: requests } = useQuery({ queryKey: ['requests'], queryFn: fetchRequests, refetchInterval: 15000 });
  const { data: catalog } = useQuery({
    queryKey: ['service-catalog'],
    queryFn: async () => { const { data } = await axios.get(`${BACKEND_URL}/api/concierge/catalog`, { headers: { Authorization: `Bearer ${token}` } }); return data; },
  });

  // Load chat history on mount
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/concierge/history?session_id=${sessionId}`, { headers: { Authorization: `Bearer ${token}` } });
        if (data?.length) {
          setMessages(data.map(m => ({ role: m.role, content: m.content, ticket_id: m.ticket_id, intent: m.intent, timestamp: m.timestamp })));
        }
      } catch {}
    })();
  }, [sessionId, token]);

  const sendMutation = useMutation({
    mutationFn: (msg) => sendConciergeMessage(msg, sessionId),
    onSuccess: (botMsg) => {
      setMessages(prev => [...prev, { role: 'assistant', content: botMsg.content, ticket_id: botMsg.ticket_id, intent: botMsg.intent, requires_approval: botMsg.requires_approval, timestamp: botMsg.timestamp }]);
      if (botMsg.ticket_id) queryClient.invalidateQueries(['requests']);
    },
    onError: () => {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.', timestamp: new Date().toISOString() }]);
    },
  });

  const surveyMutation = useMutation({
    mutationFn: async ({ ticketId, rating, feedback }) => {
      const { data } = await axios.post(`${BACKEND_URL}/api/concierge/satisfaction`, { session_id: sessionId, ticket_id: ticketId, rating, feedback }, { headers: { Authorization: `Bearer ${token}` } });
      return data;
    },
    onSuccess: () => { toast.success('Thanks for your feedback!'); setSurveyTicket(null); },
  });

  const handleSend = (text = input) => {
    const msg = text.trim();
    if (!msg) return;
    setMessages(prev => [...prev, { role: 'user', content: msg, timestamp: new Date().toISOString() }]);
    setInput('');
    sendMutation.mutate(msg);
  };

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const resolvedTickets = (requests || []).filter(r => r.status === 'RESOLVED' && !r.satisfaction);

  return (
    <div data-testid="requests-page" className="w-full max-w-[1600px] mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="font-heading text-xl font-semibold text-[var(--nexus-text-primary)] tracking-tight">Service Requests</h1>
          <p className="text-xs text-[var(--nexus-text-muted)] font-mono mt-0.5">CONCIERGE v2 — intelligent service catalog + conversational AI</p>
        </div>
        <button onClick={() => setShowCatalog(!showCatalog)} data-testid="toggle-catalog"
          className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#0d3d4d] border border-cyan-500/30 text-[var(--nexus-accent)] text-xs font-semibold hover:bg-[#0f4a5c] transition-colors">
          <Inbox size={12} /> {showCatalog ? 'Hide Catalog' : 'Service Catalog'}
        </button>
      </div>

      {/* Service Catalog */}
      {showCatalog && catalog && (
        <div className="nexus-card relative overflow-hidden mb-4 animate-nexus-slide-in" data-testid="service-catalog">
          <div className="p-3 border-b border-[var(--nexus-border)]">
            <h2 className="text-sm font-semibold text-[var(--nexus-text-primary)]">Service Catalog ({Object.keys(catalog).length} services)</h2>
          </div>
          <div className="p-3 grid grid-cols-1 md:grid-cols-3 gap-2">
            {Object.entries(catalog).map(([key, svc]) => {
              const Icon = CATALOG_ICONS[svc.icon] || HelpCircle;
              const catColor = CATALOG_COLORS[svc.category] || '#94A3B8';
              const slaText = svc.sla_hours < 24 ? `${svc.sla_hours}h` : `${svc.sla_hours / 24}d`;
              return (
                <button key={key} onClick={() => handleSend(`I need help with: ${svc.name}`)} data-testid={`catalog-${key}`}
                  className="flex items-start gap-2.5 p-3 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)] hover:bg-white/[0.04] text-left transition-colors">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${catColor}15`, color: catColor }}>
                    <Icon size={14} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-xs font-semibold text-[var(--nexus-text-primary)]">{svc.name}</span>
                    <p className="text-[10px] text-[var(--nexus-text-muted)] mt-0.5">{svc.description}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="font-mono text-[9px] px-1 py-0 rounded bg-[var(--nexus-bg-elevated)] border border-[var(--nexus-border)] text-[var(--nexus-text-muted)]">SLA: {slaText}</span>
                      <span className={`font-mono text-[9px] px-1 py-0 rounded border ${svc.requires_approval ? 'bg-[#4d3000] text-[#fcd34d] border-amber-500/30' : 'bg-[#0d3d1d] text-green-300 border-green-500/30'}`}>
                        {svc.requires_approval ? `Needs ${svc.approval_role?.replace('_',' ')}` : 'Auto-fulfillable'}
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Left: Chat */}
        <div className="lg:col-span-3">
          <div className="nexus-card relative overflow-hidden flex flex-col" style={{ height: '560px' }}>
            <div className="p-3 border-b border-[var(--nexus-border)]">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-[#0d3d1d] flex items-center justify-center">
                  <Bot size={14} className="text-[var(--nexus-success)]" />
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-[var(--nexus-text-primary)]">CONCIERGE v2</h2>
                  <div className="flex items-center gap-1"><StatusDot status="active" pulse size="sm" /><span className="font-mono text-[10px] text-[var(--nexus-success)]">Online — {Object.keys(catalog || {}).length} services</span></div>
                </div>
              </div>
            </div>

            <ScrollArea className="flex-1">
              <div className="p-4 space-y-3" data-testid="concierge-chat">
                {messages.length === 0 && (
                  <div className="animate-nexus-slide-in">
                    <div className="flex items-start gap-2.5">
                      <div className="w-6 h-6 rounded-full bg-[#0d3d1d] flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Sparkles size={11} className="text-[var(--nexus-success)]" />
                      </div>
                      <div className="p-3 rounded-xl bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)] max-w-[80%]">
                        <p className="text-xs text-[var(--nexus-text-secondary)] leading-relaxed">Hi! I'm CONCIERGE, your AI IT assistant. I handle password resets, VPN access, software installation, laptop requests, and more. Check the <strong>Service Catalog</strong> above for all available services with SLAs. How can I help?</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-3 ml-8">
                      {QUICK_ACTIONS.map(qa => (
                        <button key={qa.text} onClick={() => handleSend(qa.text)}
                          className="px-2.5 py-1 rounded-full bg-[var(--nexus-bg-elevated)] border border-[var(--nexus-border)] text-[10px] text-[var(--nexus-text-secondary)] hover:bg-white/[0.06] hover:text-[var(--nexus-accent)] transition-colors">
                          {qa.text}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {messages.map((msg, i) => (
                  <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'items-start gap-2.5'} animate-nexus-slide-in`}>
                    {msg.role === 'assistant' && (
                      <div className="w-6 h-6 rounded-full bg-[#0d3d1d] flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Bot size={11} className="text-[var(--nexus-success)]" />
                      </div>
                    )}
                    <div className={`p-3 rounded-xl max-w-[80%] ${msg.role === 'user' ? 'bg-[#0d2847] border border-blue-500/20' : 'bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)]'}`}>
                      <p className="text-xs text-[var(--nexus-text-secondary)] leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                      {msg.ticket_id && (
                        <div className="mt-2 flex items-center gap-2">
                          <Link to={`/incidents/${msg.ticket_id}`} className="inline-flex items-center gap-1 font-mono text-[10px] text-[var(--nexus-accent)] hover:underline">
                            <ExternalLink size={10} /> {msg.ticket_id}
                          </Link>
                          <button onClick={() => setSurveyTicket(msg.ticket_id)} className="text-[9px] text-[var(--nexus-text-muted)] hover:text-[var(--nexus-accent)]">Rate this</button>
                        </div>
                      )}
                      {msg.requires_approval && (
                        <div className="mt-1.5 flex items-center gap-1 text-[10px] text-[var(--nexus-warning)]">
                          <Shield size={10} /> Requires approval
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {sendMutation.isPending && (
                  <div className="flex items-start gap-2.5">
                    <div className="w-6 h-6 rounded-full bg-[#0d3d1d] flex items-center justify-center">
                      <Bot size={11} className="text-[var(--nexus-success)]" />
                    </div>
                    <div className="flex gap-1 p-3">
                      <span className="w-1.5 h-1.5 rounded-full bg-[var(--nexus-success)] animate-bounce" style={{animationDelay:'0ms'}} />
                      <span className="w-1.5 h-1.5 rounded-full bg-[var(--nexus-success)] animate-bounce" style={{animationDelay:'150ms'}} />
                      <span className="w-1.5 h-1.5 rounded-full bg-[var(--nexus-success)] animate-bounce" style={{animationDelay:'300ms'}} />
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>
            </ScrollArea>

            {/* Satisfaction Survey Modal */}
            {surveyTicket && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-20" data-testid="satisfaction-survey">
                <div className="nexus-card p-4 w-72">
                  <h3 className="text-sm font-semibold text-[var(--nexus-text-primary)] mb-2">Rate Your Experience</h3>
                  <p className="text-[10px] text-[var(--nexus-text-muted)] mb-3">How was your experience with {surveyTicket}?</p>
                  <div className="flex gap-1 mb-3">
                    {[1,2,3,4,5].map(star => (
                      <button key={star} onClick={() => surveyMutation.mutate({ ticketId: surveyTicket, rating: star, feedback: '' })}
                        className="p-2 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)] hover:bg-amber-500/10 hover:border-amber-500/30 transition-colors" data-testid={`star-${star}`}>
                        <Star size={16} className="text-[var(--nexus-warning)]" fill={star <= 3 ? 'none' : 'currentColor'} />
                      </button>
                    ))}
                  </div>
                  <button onClick={() => setSurveyTicket(null)} className="text-[10px] text-[var(--nexus-text-muted)] hover:text-[var(--nexus-text-secondary)]">Skip</button>
                </div>
              </div>
            )}

            <div className="p-3 border-t border-[var(--nexus-border)]">
              <form onSubmit={(e) => { e.preventDefault(); handleSend(); }} className="flex items-center gap-2">
                <input data-testid="concierge-input" type="text" value={input} onChange={e => setInput(e.target.value)}
                  placeholder="Type your request..." disabled={sendMutation.isPending}
                  className="flex-1 px-3 py-2 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)] text-sm text-[var(--nexus-text-primary)] placeholder:text-[var(--nexus-text-muted)] outline-none focus:border-[var(--nexus-accent)] disabled:opacity-50" />
                <button type="submit" disabled={!input.trim() || sendMutation.isPending} data-testid="concierge-send"
                  className="p-2 rounded-lg bg-[#0d3d1d] text-[var(--nexus-success)] hover:bg-green-500/20 disabled:opacity-30 transition-colors">
                  <Send size={16} />
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* Right: Recent Requests */}
        <div className="lg:col-span-2">
          <div className="nexus-card relative overflow-hidden">
            <div className="p-3 border-b border-[var(--nexus-border)]">
              <div className="flex items-center gap-2">
                <Inbox size={14} className="text-[var(--nexus-primary-bright)]" />
                <h2 className="text-sm font-semibold text-[var(--nexus-text-primary)]">Recent Requests</h2>
              </div>
            </div>
            <ScrollArea className="h-[500px]">
              <div className="p-2 space-y-1.5" data-testid="requests-list">
                {(requests || []).map(req => {
                  const meta = req.request_metadata || {};
                  return (
                    <Link key={req.id} to={`/incidents/${req.id}`}
                      className="flex items-start gap-2.5 p-2.5 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)] hover:bg-white/[0.03] transition-colors">
                      <div className="w-7 h-7 rounded-lg bg-[#0d3d1d] flex items-center justify-center flex-shrink-0">
                        <MessageSquare size={12} className="text-[var(--nexus-success)]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="font-mono text-[10px] text-[var(--nexus-primary-bright)]">{req.id}</span>
                          <StatusDot status={req.status === 'RESOLVED' ? 'success' : req.status === 'PENDING_APPROVAL' ? 'warning' : 'processing'} pulse={req.status !== 'RESOLVED'} size="sm" />
                          <span className="text-[10px] text-[var(--nexus-text-muted)]">{req.status}</span>
                        </div>
                        <p className="text-xs text-[var(--nexus-text-primary)] mt-0.5 truncate">{req.title}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="font-mono text-[10px] text-[var(--nexus-accent)]">{req.assigned_agent}</span>
                          {meta.intent && <span className="text-[9px] text-[var(--nexus-text-muted)]">{meta.intent}</span>}
                          {meta.requires_approval && <span className="text-[9px] text-[var(--nexus-warning)]">Needs approval</span>}
                        </div>
                      </div>
                      <ChevronRight size={12} className="text-[var(--nexus-text-muted)] flex-shrink-0 mt-2" />
                    </Link>
                  );
                })}
                {(!requests || requests.length === 0) && (
                  <div className="text-center py-8 text-[var(--nexus-text-muted)] text-xs">No service requests yet. Try chatting with CONCIERGE!</div>
                )}
              </div>
            </ScrollArea>
          </div>
        </div>
      </div>
    </div>
  );
}
